import { useState } from "react";
import {
  Avatar,
  Button,
  Card,
  DateRangePicker,
  Drawer,
  EmptyState,
  Field,
  Modal,
  Money,
  Select,
  Skeleton,
  StatusChip,
  Table,
  Tabs,
  ThemeToggle,
  useToast,
  type DateRange,
  type TableColumn,
} from "../components";
import styles from "./Gallery.module.css";

/**
 * /gallery — every §9 component in every meaningful state, both
 * themes accessible via the top-right toggle.
 *
 * Route is dev-only in production; kept in the bundle for now while
 * we're iterating on the design system.
 */

interface DemoTx {
  id: string;
  date: string;
  counterparty: string;
  handle: string;
  amountMinor: bigint;
  status: "pending" | "completed" | "failed" | "reversed";
}

const DEMO_TX: DemoTx[] = [
  { id: "1", date: "Aug 2", counterparty: "Northwind Supply", handle: "@northwind", amountMinor: -124000n, status: "completed" },
  { id: "2", date: "Aug 2", counterparty: "Stripe Payouts", handle: "@stripe", amountMinor: 381244n, status: "completed" },
  { id: "3", date: "Aug 1", counterparty: "Casey Nguyen", handle: "@caseyn", amountMinor: -64000n, status: "pending" },
  { id: "4", date: "Jul 30", counterparty: "Harbor Freight Co", handle: "@harborfr", amountMinor: -41290n, status: "failed" },
  { id: "5", date: "Jul 30", counterparty: "Dana Whitfield", handle: "@danaw", amountMinor: 6400n, status: "reversed" },
];

const TABS_STATUS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "reversed", label: "Reversed" },
] as const;

export function Gallery() {
  const [tab, setTab] = useState<(typeof TABS_STATUS)[number]["value"]>("all");
  const [fieldValue, setFieldValue] = useState("");
  const [fieldErrValue, setFieldErrValue] = useState("bad@x");
  const [selectValue, setSelectValue] = useState("usd");
  const [range, setRange] = useState<DateRange>({ start: null, end: null });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const toast = useToast();

  const columns: TableColumn<DemoTx>[] = [
    { key: "date", label: "Date", width: 110 },
    {
      key: "counterparty",
      label: "Counterparty",
      render: (r) => (
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar initials={r.counterparty.slice(0, 2).toUpperCase()} size={28} />
          <span>
            <div style={{ fontWeight: 500 }}>{r.counterparty}</div>
            <div style={{ color: "var(--text-tertiary)", fontSize: 11 }}>{r.handle}</div>
          </span>
        </span>
      ),
    },
    {
      key: "amount",
      label: "Amount",
      align: "right",
      width: 150,
      render: (r) => (
        <Money
          value={r.amountMinor}
          asset="USD"
          size="amount"
          intent={
            r.status === "failed" || r.status === "reversed"
              ? "muted"
              : r.amountMinor > 0n
                ? "credit"
                : "default"
          }
        />
      ),
    },
    {
      key: "status",
      label: "Status",
      width: 130,
      render: (r) => <StatusChip status={r.status} />,
    },
  ];

  return (
    <main className={styles.page}>
      <ThemeToggle />

      <div className={styles.head}>
        <div>
          <p className={styles.eyebrow}>§9 · Component gallery</p>
          <h1 className={styles.title}>AsterWallet — components</h1>
        </div>
      </div>
      <p className={styles.sectionNote}>
        Every component below reads from{" "}
        <code style={{ fontFamily: "var(--font-mono)", fontSize: "0.9em" }}>
          design-tokens.ts
        </code>{" "}
        via the generated tokens.css. Toggle themes with the chip in the top
        right; every value below re-resolves. Grep the source for hex literals —
        expect zero outside{" "}
        <code style={{ fontFamily: "var(--font-mono)", fontSize: "0.9em" }}>
          tokens.generated.css
        </code>
        .
      </p>

      {/* --- Money --- */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Money</h2>
        <p className={styles.sectionNote}>
          Takes minor units as <code>bigint</code>. Cents at 55%. Tabular. Intent decides color; sign never does.
        </p>
        <div className={styles.gridWide}>
          <div className={styles.demoTile}>
            <p className={styles.demoLabel}>Hero (balance)</p>
            <Money value={1248036n} asset="USD" size="hero" />
          </div>
          <div className={styles.demoTile}>
            <p className={styles.demoLabel}>Amount · credit</p>
            <Money value={1894000n} asset="USD" size="amount" intent="credit" />
          </div>
          <div className={styles.demoTile}>
            <p className={styles.demoLabel}>Amount · default (outgoing)</p>
            <Money value={-941278n} asset="USD" size="amount" />
          </div>
          <div className={styles.demoTile}>
            <p className={styles.demoLabel}>Amount · muted (failed row)</p>
            <Money value={-41290n} asset="USD" size="amount" intent="muted" />
          </div>
          <div className={styles.demoTile}>
            <p className={styles.demoLabel}>Loading</p>
            <Money value={1248036n} asset="USD" size="hero" loading />
          </div>
          <div className={styles.demoTile}>
            <p className={styles.demoLabel}>Crypto</p>
            <Money value={14820000n} asset="BTC" size="amount" />
          </div>
        </div>
      </section>

      {/* --- StatusChip --- */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>StatusChip</h2>
        <p className={styles.sectionNote}>
          Four states only. `Cleared` is deliberately absent (no backend meaning).
        </p>
        <div className={styles.row}>
          <StatusChip status="pending" />
          <StatusChip status="completed" />
          <StatusChip status="failed" />
          <StatusChip status="reversed" />
        </div>
      </section>

      {/* --- Button --- */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Button</h2>
        <p className={styles.sectionNote}>
          Destructive is ghost + ember, never filled red. `disabledReason` renders as helper.
        </p>
        <div className={styles.stack}>
          <div className={styles.row}>
            <Button variant="primary">Add money</Button>
            <Button variant="secondary">Send</Button>
            <Button variant="ghost">See details</Button>
            <Button variant="destructive">Remove funding source</Button>
          </div>
          <div className={styles.row}>
            <Button variant="primary" size="sm">Approve on phone</Button>
            <Button variant="secondary" size="sm">Cancel request</Button>
            <Button variant="primary" loading>Sending…</Button>
            <Button variant="primary" disabled>Balance too low</Button>
          </div>
          <div className={styles.row}>
            <Button
              variant="primary"
              disabledReason="You need a registered device to send money."
            >
              Send
            </Button>
          </div>
        </div>
      </section>

      {/* --- Table --- */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Table</h2>
        <p className={styles.sectionNote}>
          Sticky header, right-aligned tabular amount column, row hover surface-raised.
        </p>
        <Card padding={0}>
          <Table
            columns={columns}
            rows={DEMO_TX}
            rowKey={(r) => r.id}
            onRowClick={() => setDrawerOpen(true)}
            empty={
              <EmptyState title="No activity yet" body="Transactions appear here as they clear." />
            }
          />
        </Card>
      </section>

      {/* --- Field / Select / DateRangePicker --- */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Field · Select · DateRangePicker</h2>
        <p className={styles.sectionNote}>
          Error replaces helper (never stacks). Counter, when set, shows on the right.
        </p>
        <div className={styles.gridWide}>
          <Field
            label="Handle"
            value={fieldValue}
            onChange={setFieldValue}
            helper="What people see when you get paid."
            counter={20}
          />
          <Field
            label="Email"
            value={fieldErrValue}
            onChange={setFieldErrValue}
            error="That email address isn't valid."
            type="email"
          />
          <Select
            label="Funding source"
            value={selectValue}
            onChange={setSelectValue}
            options={[
              { value: "usd", label: "Aster balance · $12,480.36" },
              { value: "chase", label: "Chase •••• 4471" },
              { value: "boa", label: "Bank of America •••• 8820" },
            ]}
            helper="Used when balance can't cover the amount."
          />
          <DateRangePicker
            label="Date range"
            value={range}
            onChange={setRange}
          />
        </div>
      </section>

      {/* --- Avatar --- */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Avatar</h2>
        <div className={styles.row}>
          <Avatar initials="JD" size={24} />
          <Avatar initials="JD" size={28} />
          <Avatar initials="JD" size={32} />
          <Avatar initials="JD" size={44} />
          <Avatar initials="JD" size={44} shape="circle" />
        </div>
      </section>

      {/* --- Card --- */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Card</h2>
        <div className={styles.gridWide}>
          <Card>
            <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: 12 }}>
              Standard card — surface + hairline + radius-card.
            </p>
          </Card>
          <Card pressable>
            <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: 12 }}>
              Pressable card — hover to see surface-raised, tap for surface-pressed.
            </p>
          </Card>
        </div>
      </section>

      {/* --- Tabs --- */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Tabs (segmented)</h2>
        <div className={styles.stack}>
          <Tabs options={TABS_STATUS} value={tab} onChange={setTab} />
          <Tabs options={TABS_STATUS} value={tab} onChange={setTab} size="sm" />
        </div>
      </section>

      {/* --- Drawer + Modal --- */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Drawer &amp; Modal</h2>
        <p className={styles.sectionNote}>
          Both trap focus, dismiss on Escape / scrim click / Close button, and never commit on dismiss.
        </p>
        <div className={styles.row}>
          <Button variant="secondary" onClick={() => setDrawerOpen(true)}>
            Open detail drawer
          </Button>
          <Button variant="secondary" onClick={() => setModalOpen(true)}>
            Open approval modal
          </Button>
        </div>
      </section>

      {/* --- Toast --- */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Toast</h2>
        <div className={styles.row}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              toast.push({
                title: "Transfer sent",
                body: "Northwind Supply · $1,240.00",
                intent: "completed",
              })
            }
          >
            Completed
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              toast.push({
                title: "Awaiting confirmation",
                body: "Casey Nguyen · $640.00",
                intent: "pending",
              })
            }
          >
            Pending
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              toast.push({
                title: "Payment declined",
                body: "Insufficient funds. Try a different source.",
                intent: "failed",
              })
            }
          >
            Failed
          </Button>
        </div>
      </section>

      {/* --- EmptyState --- */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>EmptyState</h2>
        <p className={styles.sectionNote}>An invitation, not an apology.</p>
        <Card padding={0}>
          <EmptyState
            title="No transactions yet"
            body="Add money or receive a payment to see activity here."
            action={<Button variant="primary" size="sm">Add money</Button>}
          />
        </Card>
      </section>

      {/* --- Skeleton --- */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Skeleton</h2>
        <p className={styles.sectionNote}>
          Slow opacity pulse. Never a moving shimmer. The balance never skeletons.
        </p>
        <div className={styles.gridWide}>
          <div className={styles.demoTile}>
            <p className={styles.demoLabel}>Row</p>
            <div className={styles.stack}>
              <Skeleton variant="row" />
              <Skeleton variant="row" />
              <Skeleton variant="row" width="60%" />
            </div>
          </div>
          <div className={styles.demoTile}>
            <p className={styles.demoLabel}>Text</p>
            <div className={styles.stack}>
              <Skeleton variant="text" />
              <Skeleton variant="text" width="80%" />
            </div>
          </div>
          <div className={styles.demoTile}>
            <p className={styles.demoLabel}>Block</p>
            <Skeleton variant="block" height={80} />
          </div>
        </div>
      </section>

      <footer className={styles.footer}>
        Banking services provided by [SPONSOR BANK], Member FDIC. Crypto assets
        are not FDIC insured.
      </footer>

      {/* --- Overlays (rendered only when open) --- */}
      <Drawer
        open={drawerOpen}
        onDismiss={() => setDrawerOpen(false)}
        title="Payment · Northwind Supply"
      >
        <Money value={-124000n} asset="USD" size="hero" />
        <p style={{ color: "var(--text-secondary)" }}>
          Invoice 20418 · @northwind
        </p>
        <StatusChip status="completed" />
        <div style={{ marginTop: 16 }}>
          <Button variant="secondary">Send again</Button>
        </div>
      </Drawer>

      <Modal
        open={modalOpen}
        onDismiss={() => setModalOpen(false)}
        title="Approve on your phone"
        blurScrim
        showClose={false}
      >
        <p style={{ color: "var(--text-secondary)", fontSize: 13, marginTop: 8 }}>
          Confirm this action from your registered mobile device. Web sessions
          can read your account, but every money-moving action needs hardware
          approval.
        </p>
        <div
          style={{
            width: 132,
            height: 132,
            background: "var(--surface-well)",
            borderRadius: "var(--radius-card)",
            margin: "20px auto",
            display: "grid",
            placeItems: "center",
            color: "var(--text-tertiary)",
            fontSize: 12,
          }}
        >
          QR
        </div>
        <p
          style={{
            textAlign: "center",
            color: "var(--text-secondary)",
            fontSize: 12,
            margin: 0,
          }}
        >
          Waiting · expires in 4:58
        </p>
        <div style={{ marginTop: 20 }}>
          <Button variant="secondary" fullWidth onClick={() => setModalOpen(false)}>
            Cancel request
          </Button>
        </div>
      </Modal>
    </main>
  );
}
